import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sarApi, organisationsApi, deadlinesApi } from '../../lib/api';
import { Button } from '../ui/button';
import AppShell from '../AppShell';
import { PageHeader } from '../PageHeader';
import { SARStatusPill, StatusPill } from '../StatusPills';
import { toast } from 'sonner';

const DAY_MS = 24 * 60 * 60 * 1000;

/** `14 FEB` / `1 SEPT` — the evidence-line date. */
const monoDay = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase();
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
const count = (n) => (n < WORDS.length ? WORDS[n] : String(n));

/**
 * One sentence that states the position, ordered by who is blocked. It
 * should read like a person telling you where things stand, not a summary.
 */
const positionSentence = ({ overdue, needsAction, waiting, notStarted, received, total }) => {
  if (total === 0) return 'Nothing here yet. A request appears once you have written one.';
  const parts = [];
  if (overdue > 0) {
    parts.push(`${count(overdue)} ${overdue === 1 ? 'is' : 'are'} past ${overdue === 1 ? 'its' : 'their'} deadline.`);
  }
  if (needsAction > 0) {
    parts.push(`${count(needsAction)} ${needsAction === 1 ? 'needs' : 'need'} something from you.`);
  }
  if (waiting > 0) {
    parts.push(`${count(waiting)} ${waiting === 1 ? 'is' : 'are'} with the organisation.`);
  }
  if (notStarted > 0) {
    parts.push(`${count(notStarted)} ${notStarted === 1 ? 'has' : 'have'} not been sent yet.`);
  }
  if (received > 0) {
    parts.push(`${count(received)} ${received === 1 ? 'has' : 'have'} sent your records.`);
  }
  if (overdue === 0 && needsAction === 0) parts.push('Nothing needs you today.');
  return parts.join(' ');
};

const SARList = () => {
  const navigate = useNavigate();

  const [sars, setSars] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [deadlines, setDeadlines] = useState({});
  const [loading, setLoading] = useState(true);
  const [generatingOrgId, setGeneratingOrgId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [sarsRes, orgsRes, deadlinesRes] = await Promise.all([
        sarApi.getAll(),
        organisationsApi.getEnriched(),
        deadlinesApi.getAll(),
      ]);
      setSars(sarsRes.data.sars || []);
      setOrganisations(orgsRes.data.organisations || []);

      const deadlineMap = {};
      (deadlinesRes.data.deadlines || []).forEach((d) => {
        deadlineMap[d.sar_id] = d;
      });
      setDeadlines(deadlineMap);
    } catch {
      toast.error('Could not load your requests. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Deadline position for a sent request: the date it is due (extended if
   * the organisation asked for more time) and how many days are left.
   */
  const getDeadlineInfo = (sar) => {
    if (sar.status === 'draft' || !sar.sent_at) return null;

    const deadline = deadlines[sar.id];
    const dueDate = deadline
      ? new Date(deadline.extended_deadline || deadline.initial_deadline)
      : new Date(new Date(sar.sent_at).getTime() + 30 * DAY_MS);
    // The server's count is the one the status chip shows; only fall back to
    // our own arithmetic when it is missing, so the two never disagree.
    const daysRemaining = typeof sar.days_remaining === 'number'
      ? sar.days_remaining
      : Math.ceil((dueDate - new Date()) / DAY_MS);

    return {
      dueDate,
      daysRemaining,
      isOverdue: daysRemaining < 0,
      isExtended: !!deadline?.extended_deadline,
    };
  };

  const handleGenerateForOrg = async (org) => {
    if (!org || generatingOrgId) return;
    setGeneratingOrgId(org.id);
    try {
      const res = await sarApi.generate({
        organisation_id: org.id,
        organisation_name: org.name,
      });
      toast.success(`Request written for ${org.name}. Check it, then send it.`);
      navigate(`/sar/${res.data.id}`);
    } catch {
      toast.error('Could not write the request. Try again in a moment.');
      setGeneratingOrgId(null);
    }
  };

  // Already have at least one document from this organisation (an
  // onboarding upload, an org upload link, or a SAR response) — don't
  // prompt for a SAR just because it didn't arrive via one. Matches the
  // org_status logic in compute_org_status. document_count comes from the
  // enriched endpoint's live db.documents lookup — o.documents is a
  // legacy field nothing ever populates, so it can't be used here.
  const pendingOrgs = organisations.filter(
    (o) => o.status === 'pending' && !o.sar_id && !(o.document_count > 0),
  );

  if (loading) {
    return (
      <AppShell>
        <main aria-busy="true">
          <p className="text-ink-55">Loading your requests.</p>
        </main>
      </AppShell>
    );
  }

  // Each request goes into exactly one group, ordered by who is blocked.
  const withDeadline = sars.map((s) => ({ sar: s, info: getDeadlineInfo(s) }));
  const byDeadline = (a, b) =>
    (a.info?.daysRemaining ?? Infinity) - (b.info?.daysRemaining ?? Infinity);
  const byCreatedDesc = (a, b) =>
    new Date(b.sar.created_at || 0) - new Date(a.sar.created_at || 0);

  const needsYou = [];
  const waiting = [];
  const notStarted = [];
  const received = [];
  let overdueCount = 0;

  for (const item of withDeadline) {
    const s = item.sar;
    const m = s.milestones || {};
    const status = s.effective_status || s.status;
    const awaitingIdAction = m.id_requested_at && !m.id_sent_at;

    if (status === 'draft' || !s.sent_at) {
      notStarted.push(item);
    } else if (status === 'complete' || m.data_received_at) {
      received.push(item);
    } else if (item.info?.isOverdue || status === 'overdue') {
      overdueCount += 1;
      needsYou.push({ ...item, overdue: true });
    } else if (
      status === 'refusal' || status === 'refused' || status === 'escalated' ||
      status === 'escalated_ico' || awaitingIdAction
    ) {
      needsYou.push(item);
    } else {
      waiting.push(item);
    }
  }

  needsYou.sort(byDeadline);
  waiting.sort(byDeadline);
  notStarted.sort((a, b) => new Date(a.sar.created_at || 0) - new Date(b.sar.created_at || 0));
  received.sort(byCreatedDesc);

  const sortedPendingOrgs = [...pendingOrgs].sort((a, b) =>
    (a.name || '').localeCompare(b.name || ''),
  );

  const total = sars.length + sortedPendingOrgs.length;
  const sentence = positionSentence({
    overdue: overdueCount,
    needsAction: needsYou.length - overdueCount,
    waiting: waiting.length,
    notStarted: notStarted.length + sortedPendingOrgs.length,
    received: received.length,
    total,
  });

  /** `SENT 14 FEB · DUE 16 MAR · 9 DAYS LATE` / `SENT 1 SEPT · 14 DAYS LEFT` / `NOT SENT YET` */
  const metaLine = (sar, info) => {
    const m = sar.milestones || {};
    if (!sar.sent_at || !info) return 'NOT SENT YET';
    const bits = [`SENT ${monoDay(sar.sent_at)}`];
    if (m.data_received_at) {
      bits.push(`RECEIVED ${monoDay(m.data_received_at)}`);
      return bits.join(' · ');
    }
    if (info.isOverdue) {
      bits.push(`DUE ${monoDay(info.dueDate)}`);
      bits.push(`${plural(Math.abs(info.daysRemaining), 'DAY')} LATE`);
    } else if (m.id_requested_at && !m.id_sent_at) {
      bits.push('THEY ASKED FOR PROOF OF ID');
    } else {
      if (info.isExtended) bits.push(`DUE ${monoDay(info.dueDate)}`);
      bits.push(`${plural(info.daysRemaining, 'DAY')} LEFT`);
    }
    return bits.join(' · ');
  };

  const Row = ({ sar, info, overdue }) => {
    const status = sar.effective_status || sar.status;
    const days = typeof sar.days_remaining === 'number' ? sar.days_remaining : info?.daysRemaining;
    return (
      <button
        type="button"
        onClick={() => navigate(`/sar/${sar.id}`)}
        className={`w-full text-left rounded-lg px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 transition-colors duration-state ${
          overdue ? 'bg-breach-wash hover:bg-[#F6D2CA]' : 'bg-ground hover:bg-inset'
        }`}
        data-testid={`sar-card-${sar.id}`}
      >
        <span className="min-w-0 flex-1">
          <span className="block font-heading text-[19px] font-semibold tracking-[-0.02em] leading-[1.25] text-ink">
            {sar.organisation_name}
          </span>
          <span className={`pd-mono mt-1.5 block ${overdue ? 'text-breach-ink' : 'text-ink-55'}`}>
            {metaLine(sar, info)}
          </span>
        </span>
        <span className="shrink-0">
          <SARStatusPill status={status} daysRemaining={days} />
        </span>
      </button>
    );
  };

  const OrgRow = ({ org }) => (
    <button
      type="button"
      onClick={() => handleGenerateForOrg(org)}
      disabled={!!generatingOrgId}
      className="w-full text-left rounded-lg px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-ground hover:bg-inset transition-colors duration-state disabled:opacity-60"
      data-testid={`pending-org-card-${org.id}`}
    >
      <span className="min-w-0 flex-1">
        <span className="block font-heading text-[19px] font-semibold tracking-[-0.02em] leading-[1.25] text-ink">
          {org.name}
        </span>
        <span className="pd-mono mt-1.5 block text-ink-55">
          {generatingOrgId === org.id ? 'WRITING THE REQUEST' : 'NOT WRITTEN YET · TAP TO WRITE IT'}
        </span>
      </span>
      <span className="shrink-0">
        <StatusPill tone="neutral">Not requested yet</StatusPill>
      </span>
    </button>
  );

  const sections = [
    {
      id: 'needs-you',
      title: 'Needs you',
      rows: needsYou.map((item) => <Row key={item.sar.id} {...item} />),
    },
    {
      id: 'waiting',
      title: 'Waiting on them',
      rows: waiting.map((item) => <Row key={item.sar.id} {...item} />),
    },
    {
      id: 'not-started',
      title: 'Not started',
      rows: [
        ...sortedPendingOrgs.map((org) => <OrgRow key={`org-${org.id}`} org={org} />),
        ...notStarted.map((item) => <Row key={item.sar.id} {...item} />),
      ],
    },
    {
      id: 'received',
      title: 'Records received',
      rows: received.map((item) => <Row key={item.sar.id} {...item} />),
    },
  ].filter((s) => s.rows.length > 0);

  return (
    <AppShell>
      <main>
        <PageHeader
          title="Requests"
          lede={(
            <>
              <p data-testid="requests-position">{sentence}</p>
              <p>
                A request is your legal right under data protection law to a copy of everything an
                organisation holds about you. Councils call it a Subject Access Request.
              </p>
            </>
          )}
        />

        {total === 0 ? (
          <section className="py-6" data-testid="sar-empty-state">
            <div aria-hidden="true" className="w-[52px] h-[52px] bg-inset rounded-lg" />
            <h2 className="mt-5 font-heading text-[26px] font-semibold tracking-[-0.02em] text-ink">
              No requests yet
            </h2>
            <p className="mt-2 text-base text-ink-70 max-w-[52ch]">
              Add an organisation first. A request appears here as soon as you have written one for them.
            </p>
            <Button
              variant="secondary"
              className="mt-5"
              onClick={() => navigate('/organisations')}
              data-testid="go-to-orgs-btn"
            >
              Add an organisation
            </Button>
          </section>
        ) : (
          <div className="flex flex-col gap-9">
            {sections.map((section) => (
              <section key={section.id} data-testid={`sar-section-${section.id}`}>
                <h2 className="pd-eyebrow mb-3">
                  {section.title}
                  <span className="ml-2 font-normal">{section.rows.length}</span>
                </h2>
                <div className="flex flex-col gap-2.5">{section.rows}</div>
              </section>
            ))}
          </div>
        )}
      </main>
    </AppShell>
  );
};

export default SARList;
